import type { GameContext } from "../../GameContext";
import type { CharacterSummary } from "../../controllers/CharacterSelectController";

export type HudPanelName = "inventory" | "equipment" | "character" | "skills" | "job" | "economy" | "worldmap";

/**
 * Always-visible overlay: zone name, character name/level, connection
 * status, chat log, and buttons that toggle the six on-demand panels.
 */
export class WorldHud {
  private el: HTMLDivElement;
  private chatLog: HTMLDivElement;
  private statusEl: HTMLSpanElement;

  constructor(
    root: HTMLElement,
    private readonly ctx: GameContext,
    private readonly character: CharacterSummary,
    private readonly handlers: { onTogglePanel: (panel: HudPanelName) => void }
  ) {
    this.el = document.createElement("div");
    this.el.className = "world-hud";

    const nameLine = document.createElement("div");
    nameLine.textContent = `${character.name} — ${character.jobId} Lv.${character.baseLevel}`;
    this.el.appendChild(nameLine);

    this.statusEl = document.createElement("span");
    this.statusEl.className = "connection-status";
    this.setConnectionStatus("connected");
    this.el.appendChild(this.statusEl);

    const buttonBar = document.createElement("div");
    buttonBar.className = "hud-button-bar";
    const panels: Array<{ key: HudPanelName; label: string }> = [
      { key: "character", label: "Character" },
      { key: "inventory", label: "Inventory" },
      { key: "equipment", label: "Equipment" },
      { key: "skills", label: "Skills" },
      { key: "job", label: "Job" },
      { key: "economy", label: "Trade" },
      { key: "worldmap", label: "Map" },
    ];
    for (const p of panels) {
      const btn = document.createElement("button");
      btn.textContent = p.label;
      btn.addEventListener("click", () => this.handlers.onTogglePanel(p.key));
      buttonBar.appendChild(btn);
    }
    this.el.appendChild(buttonBar);

    this.chatLog = document.createElement("div");
    this.chatLog.className = "chat-log";
    this.el.appendChild(this.chatLog);

    root.appendChild(this.el);
  }

  setConnectionStatus(status: "connected" | "reconnecting" | "disconnected"): void {
    const labels: Record<typeof status, string> = {
      connected: "● Connected",
      reconnecting: "◐ Reconnecting…",
      disconnected: "○ Disconnected",
    } as const;
    this.statusEl.textContent = labels[status];
    this.statusEl.dataset["status"] = status;
  }

  appendChatMessage(msg: { from: string; text: string }): void {
    const line = document.createElement("div");
    line.textContent = `${msg.from}: ${msg.text}`;
    this.chatLog.appendChild(line);
    // Keep the log from growing unbounded during a long session (1.2:
    // sessions can run "several hours").
    while (this.chatLog.childNodes.length > 100) {
      this.chatLog.removeChild(this.chatLog.firstChild!);
    }
  }
}
